import Debug from 'debug'
import { callUnaryMethod } from '@connectrpc/connect-query'
import { useMutation } from '@tanstack/react-query'

import {
  forceRefreshAccessToken,
  getOrRefreshAccessToken,
} from '@jotta/auth-client/useAuthStatus'
import {
  AllocateConflictHandler,
  FileState,
} from '@jotta/grpc-connect-openapi/file'
import { allocate } from '@jotta/grpc-connect-openapi/fileQuery'
import { AbortError } from '@jotta/types/AbortError'
import type { ProgressCallback } from '@jotta/types/IO'
import { calculateFileMd5 } from '@jotta/utils/crypto'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import type { FileMeta } from '@jotta/utils/files'
import { fileUploadXhr } from '@jotta/utils/fileUploadXhr'
import { createPath } from '@jotta/utils/pathUtils'
import { nextXid } from '@jotta/utils/xid'

import { createAuthTransport } from '../transport'

const debug = Debug('useFileUpload')
export const fileUploadMutationKey = ['fileUpload'] as const

export type UploadFileInput = {
  apiPath?: string
  path?: string
  file: FileMeta
  conflictHandler?: AllocateConflictHandler
  hashChunkSize?: string | number
  onHashProgress?: ProgressCallback
  onUploadProgress?: ProgressCallback
  onUploadStart?: () => void
  signal?: AbortSignal
  token?: string
  xid?: string
}

export async function uploadFile({
  apiPath,
  conflictHandler = AllocateConflictHandler.REJECT_CONFLICTS,
  file: fileMeta,
  hashChunkSize = '1mb',
  path: pathParam,
  onHashProgress,
  onUploadProgress,
  onUploadStart,
  signal,
  token,
  xid = nextXid(),
}: UploadFileInput) {
  const file = fileMeta.file
  const path =
    pathParam ||
    createPath(
      apiPath || '/',
      fileMeta.relativePath || file.webkitRelativePath || file.name,
    )

  const md5 = await calculateFileMd5({
    file,
    chunkSizeStr: hashChunkSize,
    progressCallback: onHashProgress,
    signal,
  })

  if (signal?.aborted) {
    throw new AbortError()
  }

  let uploadToken = token || (await getOrRefreshAccessToken())
  const transport = createAuthTransport({
    tokenResolver: async () => uploadToken,
    // Only the user's own token can be refreshed; a given token is fixed
    tokenRefresher: token
      ? undefined
      : async rejectedToken => {
          uploadToken = await forceRefreshAccessToken(rejectedToken)
          return uploadToken
        },
    xid,
  })

  const allocateResponse = await callUnaryMethod(
    transport,
    allocate,
    {
      path,
      md5,
      size: BigInt(file.size),
      createdAtMillis: BigInt(file.lastModified),
      modifiedAtMillis: BigInt(file.lastModified),
      conflictHandler,
    },
    {
      signal,
    },
  )

  debug(allocateResponse)
  const { state, uploadUrl } = allocateResponse

  if (signal?.aborted) {
    throw new AbortError()
  }

  switch (state) {
    case FileState.PROCESSING:
    case FileState.ADDED:
    case FileState.UNKNOWN_FILE_STATE:
      throw new Error(`Unknown file state: ${state}`)
    case FileState.CORRUPT:
    case FileState.INCOMPLETE: {
      if (signal?.aborted) {
        throw new AbortError()
      }

      onUploadStart?.()

      return await fileUploadXhr(
        file,
        uploadUrl,
        uploadToken,
        xid,
        ev => {
          if (onUploadProgress) {
            onUploadProgress(
              ev.loaded,
              ev.lengthComputable ? ev.total : undefined,
            )
          }
        },
        signal,
      )
    }
    case FileState.COMPLETED:
      return
    default:
      return exhaustiveGuard(state)
  }
}

export function useFileUpload({
  token: tokenParam,
  onSuccess,
}: {
  token?: string
  onSuccess?: () => void
}) {
  return useMutation({
    mutationKey: fileUploadMutationKey,
    onSuccess,
    mutationFn: async ({
      path,
      file,
      conflictHandler = AllocateConflictHandler.REJECT_CONFLICTS,
    }: {
      path: string
      file: File
      conflictHandler: AllocateConflictHandler
    }) => {
      return uploadFile({
        path,
        file: { file },
        conflictHandler,
        token: tokenParam,
      })
    },
  })
}
